import { ColumnFilterItem } from 'antd/lib/table/interface'
import { TableFilterItem } from '../types'
import { useCallback } from 'react'
import { Tooltip } from 'antd'

/**
 * Converts table filter items into Ant Design filter items, removes duplicate
 * values, and preserves nested options and tooltips.
 */
export function useFilterItems() {
  const toTableFilterItem: (
    item: string | number | TableFilterItem
  ) => TableFilterItem = useCallback((item) => {
    if (typeof item === 'string' || typeof item === 'number')
      return { text: item.toString(), value: item }
    return item
  }, [])

  const makeUnique: (
    items: (string | number | TableFilterItem | undefined | null)[]
  ) => TableFilterItem[] = useCallback(
    (items) => {
      const unique = new Map<TableFilterItem['value'], TableFilterItem>()
      items.forEach((item) => {
        if (item === undefined || item === null) return
        const { text, value, children, tooltip } = toTableFilterItem(item)

        const existing = unique.get(value)

        if (!existing) {
          const newItem: TableFilterItem = { text, value, tooltip }
          if (children) newItem.children = makeUnique(children)
          unique.set(value, newItem)
        } else if (children) {
          const existingChildren = existing.children ?? []
          existing.children = makeUnique(existingChildren.concat(children))
          unique.set(value, existing)
        }
      })
      return [...unique.values()]
    },
    [toTableFilterItem]
  )

  const withTooltip: (
    items: TableFilterItem[] | undefined
  ) => ColumnFilterItem[] | undefined = useCallback(
    (items) =>
      items?.map<ColumnFilterItem>(
        ({ text: label, value, children, tooltip }) => ({
          text: tooltip ? <Tooltip title={tooltip}>{label}</Tooltip> : label,
          value,
          children: withTooltip(children),
        })
      ),
    []
  )

  return useCallback(
    (items: (string | number | TableFilterItem)[] | undefined) => {
      if (!items) return []
      return withTooltip(makeUnique(items))
    },
    [makeUnique, withTooltip]
  )
}
